
import React, { useState, useEffect, useRef } from 'react';
import { useConsentManager } from '@/hooks/useConsentManager';
import { ConsentBanner } from './ConsentBanner';

interface GDPRProviderProps {
  children: React.ReactNode;
}

export const GDPRProvider: React.FC<GDPRProviderProps> = ({ children }) => {
  const { showBanner, isLoading, gdprSettings } = useConsentManager();
  const [showDelayedBanner, setShowDelayedBanner] = useState(false);
  const contentLoadedRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasShownBanner = useRef(false);

  // Check if content is already loaded
  useEffect(() => {
    const checkContentLoaded = () => {
      if (document.readyState === 'complete' && !contentLoadedRef.current) {
        contentLoadedRef.current = true;
        return true;
      }
      return false;
    };

    // If already loaded, set immediately
    if (checkContentLoaded()) {
      return;
    }

    // Otherwise wait for load event
    const handleLoad = () => {
      if (!contentLoadedRef.current) {
        contentLoadedRef.current = true;
      }
    };

    window.addEventListener('load', handleLoad, { once: true });
    
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  // Show banner logic - only run when necessary dependencies change
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Only proceed if we should show the banner and haven't already shown it
    if (!showBanner || isLoading || !gdprSettings?.gdpr_enabled || !contentLoadedRef.current || hasShownBanner.current) {
      setShowDelayedBanner(false);
      return;
    }

    // Delay banner to ensure LCP is not affected
    timeoutRef.current = setTimeout(() => {
      setShowDelayedBanner(true);
      hasShownBanner.current = true;
    }, 150);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [showBanner, isLoading, gdprSettings?.gdpr_enabled, contentLoadedRef.current]);

  // Reset banner visibility when showBanner changes to false
  useEffect(() => {
    if (!showBanner) {
      setShowDelayedBanner(false);
      hasShownBanner.current = false;
    }
  }, [showBanner]);

  return (
    <>
      {children}
      {showDelayedBanner && <ConsentBanner />}
    </>
  );
};
